﻿import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, CheckCircle2, ChevronRight, Loader2, Play, Users, UserCheck, XCircle } from 'lucide-react';
import type { ProcessType } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatCard } from '@/components/ui/stat-card';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { cn } from '@/lib/utils';
import { Avatar } from './shared';
import { formatDate, fullName } from './utils';
import { StartProcessModal } from './StartProcessModal';
import { ProcessDrawer } from './ProcessDrawer';

type StatusFilter = '' | 'in_progress' | 'completed' | 'cancelled';

export function ProcessTab({ processType }: { processType: ProcessType }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);
  const [startOpen, setStartOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const statsQuery = useQuery({ queryKey: ['recruitment', 'stats'], queryFn: () => recruitmentService.stats() });
  const listQuery = useQuery({
    queryKey: ['recruitment', 'instances', { processType, search, status, page }],
    queryFn: () => recruitmentService.listInstances({
      process_type: processType, q: search || undefined, status: status || undefined, page, limit: 20,
    }),
  });

  const s = processType === 'joining' ? statsQuery.data?.joining : statsQuery.data?.exit;
  const startLabel = processType === 'joining' ? 'Start Onboarding' : 'Start Exit';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="In Progress" value={s?.in_progress ?? '—'} icon={Users} tone="sky" />
        <StatCard label={processType === 'joining' ? 'Joining This Month' : 'This Month'} value={s?.this_month ?? '—'} icon={CalendarDays} tone="violet" />
        <StatCard label="Overdue" value={s?.overdue ?? '—'} icon={AlertTriangle} tone="rose" />
        <StatCard label="Completed" value={s?.completed ?? '—'} icon={UserCheck} tone="emerald" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search employee…"
          containerClassName="min-w-[240px] flex-1"
        />
        <Select
          value={status || 'all'}
          onValueChange={(v) => { setStatus(v === 'all' ? '' : (v as StatusFilter)); setPage(1); }}
        >
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <PermissionGate permission={PERMISSIONS.RECRUITMENT_MANAGE}>
          <Button onClick={() => setStartOpen(true)}><Play className="h-4 w-4" /> {startLabel}</Button>
        </PermissionGate>
      </div>

      <div className="rounded-lg border border-border/60 bg-card">
        {listQuery.isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : listQuery.isError ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-destructive">
            <XCircle className="h-8 w-8 opacity-60" />
            <span className="text-sm font-medium">Failed to load processes</span>
            <Button size="sm" variant="outline" onClick={() => listQuery.refetch()}>Retry</Button>
          </div>
        ) : (listQuery.data?.data.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center gap-1 py-16 text-center text-muted-foreground">
            <Users className="h-10 w-10 opacity-40" />
            <p className="text-sm font-medium">No {processType} processes found</p>
            <p className="text-xs">Click “{startLabel}” to begin</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {listQuery.data!.data.map((i) => {
              const isDone = i.status === 'completed';
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => setDrawerId(i.id)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
                >
                  <Avatar first={i.employee_first_name} last={i.employee_last_name} photoUrl={i.photo_url} />
                  <div className="min-w-0 flex-[2]">
                    <p className="truncate text-sm font-semibold text-foreground">{fullName(i.employee_first_name, i.employee_last_name)}</p>
                    <p className="truncate text-xs text-muted-foreground">{i.employee_email}</p>
                  </div>
                  {processType === 'joining' && (
                    <div className="hidden flex-1 text-xs text-muted-foreground md:block">
                      <p className="uppercase tracking-wide">Joining</p>
                      <p className="text-foreground">{formatDate(i.joining_date)}</p>
                    </div>
                  )}
                  <div className="min-w-0 flex-[2]">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{i.completed_steps}/{i.total_steps} steps</span>
                      <span className="font-medium text-foreground">{i.progress_percentage}%</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className={cn('h-full rounded-full', isDone ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${i.progress_percentage}%` }} />
                    </div>
                    {!isDone && i.next_step_name && <p className="mt-1 truncate text-[11px] text-muted-foreground">Next: {i.next_step_name}</p>}
                  </div>
                  <span className={cn(
                    'hidden shrink-0 items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium sm:inline-flex',
                    isDone ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-400'
                      : i.status === 'cancelled' ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-400'
                      : 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-900/50 dark:bg-teal-900/20 dark:text-teal-400',
                  )}>
                    {isDone ? <><CheckCircle2 className="h-3 w-3" /> Done</> : i.status === 'cancelled' ? 'Cancelled' : 'In Progress'}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {listQuery.data && listQuery.data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="text-xs text-muted-foreground">Page {page} / {listQuery.data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= listQuery.data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {startOpen && <StartProcessModal processType={processType} onClose={() => setStartOpen(false)} onStarted={(id) => { setStartOpen(false); setDrawerId(id); }} />}
      {drawerId && <ProcessDrawer instanceId={drawerId} onClose={() => setDrawerId(null)} />}
    </div>
  );
}
